// Связка (Кукольник's W): a throw at the ground that ties every enemy hero caught in its circle to the nearest
// other anchor in that circle, with a ratchet — the two ends may come closer, never part further than the
// closest they have been since the tie.
//
// Rules, decided once here:
// - Victims: enemy heroes inside the circle that CC can touch (not under Святой круг, not invulnerable, not a
//   silhouette), nearest the centre first (ties by hero id).
// - Anchor for each victim, nearest to him, inside the circle: another such enemy hero not yet tied by this
//   throw (the two are tied to each other — one bond, both held), any living summon (either team's), a monster
//   (body overlapping the circle, not leashing home), or the nearest solid point of a tree, wall, rock, pillar,
//   building or prop overlapping the circle (sampled on a 0.25 m grid). The caster and his allies never anchor.
//   Nothing to tie anyone to: a miss; the cooldown is spent all the same.
// - Ratchet, checked once a tick after every hero has moved (holdBonds): at or under the allowed distance, the
//   allowed distance shrinks to it; past it — however he got there, a walk, a dash, a knockback, a teleport —
//   the hero is put back onto it along the line to the other end. A hero–hero pair splits the correction evenly
//   (the whole of it to whichever one can still stand where it lands). A summon, a monster or an obstacle is
//   never moved: if a summon or a monster walks off, the hero is dragged after it — only as far as the allowed
//   distance needs. It never gives up: where that spot can't be stood on (an obstacle's own margin, a wall), the
//   nearest walkable spot still within the limit, else back to where the bond last held (`lastA`/`lastB`) —
//   so walking around a tree, or two heroes both wedged against walls, can't slip the tie.
// - Ends at its time, when either end dies (a summon or a monster included), or a new Связка ties the hero.
// - Hard CC: binding interrupts what the hero is doing; Смирение shortens it (a pair lasts as long as its
//   shorter half).
import * as THREE from 'three';
import { solidNear } from './layout.js';
import { bulkOf, isHero, isMonster, isSummon, monsterVisibleTo, type Target } from './monsters.js';
import { summonVisibleTo } from './summons.js';
import type { Team } from './layout.js';
import type { Game, Hero } from './game.js';

/** A spot on an obstacle: never moves, never dies. */
export type FixedEnd = { pos: THREE.Vector3; alive: true };
export type BondEnd = Target | FixedEnd;
/** `lastA`/`lastB`: where the ends stood the last time the bond held — the fallback a blocked correction returns to. */
export type Bond = { a: Hero; b: BondEnd; max: number; until: number; lastA: THREE.Vector3; lastB: THREE.Vector3 };

const flatDist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);
export const isFixedEnd = (e: BondEnd): e is FixedEnd => !('id' in e);
const OBSTACLE_STEP = 0.25;

/** The other end of `h`'s bond. */
export function otherEnd(bond: Bond, h: Hero): BondEnd { return bond.a === h ? bond.b : bond.a; }

/** Whether `team` sees this end where it is: an obstacle is map data, a unit by its own fog rule. */
export function endSeenBy(g: Game, team: Team, e: BondEnd): boolean {
  if (isFixedEnd(e)) return true;
  if (isSummon(e)) return summonVisibleTo(g, team, e);
  if (isMonster(e)) return monsterVisibleTo(g, team, e);
  return g.visibleTo(team, e);
}

function bindable(g: Game, e: Hero, caster: Hero): boolean {
  return e.alive && e.team !== caster.team && !g.silhouette(e) && !g.protectedBy(e) && g.t >= e.invulnUntil;
}

/* The nearest solid spot to `from` inside the circle, or null. `isSolid`: solid() limited to what reaches the circle. */
function nearestObstacle(c: { x: number; z: number }, r: number, from: { x: number; z: number }, isSolid: (x: number, z: number) => boolean): { x: number; z: number } | null {
  let best: { x: number; z: number } | null = null, bestD = Infinity;
  const n = Math.floor(r / OBSTACLE_STEP);
  for (let i = -n; i <= n; i++) for (let j = -n; j <= n; j++) {
    const dx = i * OBSTACLE_STEP, dz = j * OBSTACLE_STEP;
    if (dx * dx + dz * dz > r * r) continue;
    const x = c.x + dx, z = c.z + dz;
    if (!isSolid(x, z)) continue;
    const d = Math.hypot(x - from.x, z - from.z);
    if (d < bestD) { best = { x, z }; bestD = d; }
  }
  return best;
}

/** Who a throw at `c` ties to what — the one rule both the cast and the bots read. */
export function bindPlan(g: Game, caster: Hero, c: { x: number; z: number }, r: number): { a: Hero; b: BondEnd }[] {
  const victims = g.heroes.filter(e => bindable(g, e, caster) && flatDist(e.pos, c) <= r)
    .sort((x, y) => flatDist(x.pos, c) - flatDist(y.pos, c) || x.id - y.id);
  const used = new Set<Hero>(), plan: { a: Hero; b: BondEnd }[] = [];
  if (!victims.length) return plan;
  const isSolid = solidNear(c.x, c.z, r);
  for (const v of victims) {
    if (used.has(v)) continue;
    let best: BondEnd | null = null, bestD = Infinity;
    const offer = (end: BondEnd, p: { x: number; z: number }) => { const d = flatDist(p, v.pos); if (d < bestD) { best = end; bestD = d; } };
    for (const e of victims) if (e !== v && !used.has(e)) offer(e, e.pos);
    for (const s of g.summons) if (s.alive && flatDist(s.pos, c) <= r + bulkOf(s)) offer(s, s.pos);
    for (const m of g.monsters) if (m.alive && m.state !== 'return' && flatDist(m.pos, c) <= r + bulkOf(m)) offer(m, m.pos);
    const o = isSolid && nearestObstacle(c, r, v.pos, isSolid);
    if (o) offer({ pos: new THREE.Vector3(o.x, 0, o.z), alive: true }, o);
    if (!best) continue;
    used.add(v);
    const b: BondEnd = best;
    if (!isFixedEnd(b) && isHero(b)) used.add(b);
    plan.push({ a: v, b });
  }
  return plan;
}

/** Ties `a` to `b` for `dur` s (Смирение shortening either hero's share). Ends any bond either hero was in. */
export function bind(g: Game, a: Hero, b: BondEnd, dur: number): Bond {
  const bHero = !isFixedEnd(b) && isHero(b) ? b : null;
  for (const h of bHero ? [a, bHero] : [a]) {
    if (h.tether) endBond(h.tether);
    dur = Math.min(dur, g.ccFor(h, dur, g.t));
  }
  const bond: Bond = { a, b, max: flatDist(a.pos, b.pos), until: g.t + dur, lastA: a.pos.clone(), lastB: new THREE.Vector3(b.pos.x, 0, b.pos.z) };
  for (const h of bHero ? [a, bHero] : [a]) {
    h.tether = bond;
    h.pending = null; h.pendingTotem = null;
    if (h.state !== 'normal') g.interrupt(h);
    g.emit({ type: 'fx', name: 'tether', hero: h.id });
  }
  return bond;
}

export function endBond(bond: Bond) {
  if (bond.a.tether === bond) bond.a.tether = null;
  const b = bond.b;
  if (!isFixedEnd(b) && isHero(b) && b.tether === bond) b.tether = null;
}

/* Puts `h` at (x, z) if he can stand there; a slide that got him there is spent. */
function place(g: Game, h: Hero, x: number, z: number): boolean {
  if (!g.nav.isWalkable(x, z)) return false;
  h.pos.set(x, 0, z);
  h.kb.set(0, 0, 0);
  return true;
}
/* Back to a spot the bond already held at (walkable when he stood there). */
function revert(h: Hero, to: THREE.Vector3) { h.pos.set(to.x, 0, to.z); h.kb.set(0, 0, 0); }

const EPS = 1e-6;

/** The ratchet, once a tick after the heroes have moved — see the header. */
export function holdBonds(g: Game) {
  const done = new Set<Bond>();
  for (const h of g.heroes) {
    const bond = h.tether;
    if (!bond || done.has(bond)) continue;
    done.add(bond);
    const { a, b } = bond;
    if (g.t >= bond.until || !a.alive || !b.alive || (!isFixedEnd(b) && isMonster(b) && b.state === 'return')) { endBond(bond); continue; }
    const d = flatDist(a.pos, b.pos);
    if (d > bond.max + EPS) {
      const over = d - bond.max, ux = (a.pos.x - b.pos.x) / d, uz = (a.pos.z - b.pos.z) / d;
      if (!isFixedEnd(b) && isHero(b)) {
        const aOk = place(g, a, a.pos.x - ux * over / 2, a.pos.z - uz * over / 2);
        const bOk = place(g, b, b.pos.x + ux * over / 2, b.pos.z + uz * over / 2);
        if (aOk && !bOk) place(g, a, a.pos.x - ux * over / 2, a.pos.z - uz * over / 2);
        else if (bOk && !aOk) place(g, b, b.pos.x + ux * over / 2, b.pos.z + uz * over / 2);
        if (flatDist(a.pos, b.pos) > bond.max + EPS) { revert(a, bond.lastA); revert(b, bond.lastB); }
      } else {
        const x = b.pos.x + ux * bond.max, z = b.pos.z + uz * bond.max;
        if (!place(g, a, x, z)) {
          const p = g.nav.nearestWalkable(x, z);
          if (!(flatDist(p, b.pos) <= bond.max + EPS && place(g, a, p.x, p.z))) revert(a, bond.lastA);
        }
      }
    }
    const now = flatDist(a.pos, b.pos);
    if (now <= bond.max + EPS) {
      bond.max = Math.min(bond.max, now);
      bond.lastA.set(a.pos.x, 0, a.pos.z);
      bond.lastB.set(b.pos.x, 0, b.pos.z);
    }
  }
}
